import React, { createContext, useCallback, useContext, useId, useMemo, useRef, useState } from 'react';
import { focusRing } from './tokens';

// Accessible tabs primitive following WAI-ARIA Authoring Practices.
//
//   <Tabs value={tab} onValueChange={setTab} ariaLabel="Sections">
//     <TabList>
//       <Tab value="projects">Projects</Tab>
//       <Tab value="files">Files</Tab>
//     </TabList>
//     <TabPanel value="projects">...</TabPanel>
//     <TabPanel value="files">...</TabPanel>
//   </Tabs>
//
// Behavior:
//   - role="tablist" / role="tab" / role="tabpanel" + aria-selected,
//     aria-controls, aria-labelledby wired automatically.
//   - Arrow Left/Right cycles between tabs (with wrap). Home/End jump to
//     first/last. Activation moves focus AND selects the tab.
//   - Tab key from the tablist takes the user into the active panel.
//   - Each tab/panel pair shares a generated id prefix so multiple Tabs
//     instances can coexist on a page.

const TabsContext = createContext(null);

export const Tabs = ({ value, onValueChange, ariaLabel, defaultValue, children, className = '' }) => {
  // Support both controlled (value + onValueChange) and uncontrolled
  // (defaultValue) usage.
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;
  const setCurrent = useCallback(
    (next) => {
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  const idPrefix = useId();
  const tabRefs = useRef(new Map());

  const ctx = useMemo(
    () => ({ current, setCurrent, ariaLabel, idPrefix, tabRefs }),
    [current, setCurrent, ariaLabel, idPrefix],
  );

  return (
    <TabsContext.Provider value={ctx}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
};

export const TabList = ({ className = '', children, ...rest }) => {
  const ctx = useContext(TabsContext);
  const tabsArray = React.Children.toArray(children).filter(Boolean);

  const onKeyDown = (e) => {
    const values = tabsArray.map((c) => c.props.value);
    const currentIdx = values.indexOf(ctx.current);
    let nextIdx = -1;
    if (e.key === 'ArrowRight') nextIdx = (currentIdx + 1) % values.length;
    else if (e.key === 'ArrowLeft') nextIdx = (currentIdx - 1 + values.length) % values.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = values.length - 1;
    if (nextIdx >= 0) {
      e.preventDefault();
      const nextValue = values[nextIdx];
      ctx.setCurrent(nextValue);
      const el = ctx.tabRefs.current.get(nextValue);
      el?.focus();
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ctx.ariaLabel}
      onKeyDown={onKeyDown}
      className={['inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
};

export const Tab = ({ value, children, disabled = false, leadingIcon: LeadingIcon = null, className = '' }) => {
  const ctx = useContext(TabsContext);
  const tabId = `${ctx.idPrefix}-tab-${value}`;
  const panelId = `${ctx.idPrefix}-panel-${value}`;
  const isSelected = ctx.current === value;

  // tabRefs is a ref (inherently mutable) — not a valid dependency
  const ref = useCallback(
    (el) => {
      if (el) ctx.tabRefs.current.set(value, el);
      else ctx.tabRefs.current.delete(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value],
  );

  return (
    <button
      ref={ref}
      type="button"
      id={tabId}
      role="tab"
      aria-selected={isSelected}
      aria-controls={panelId}
      tabIndex={isSelected ? 0 : -1}
      disabled={disabled}
      onClick={() => !disabled && ctx.setCurrent(value)}
      className={[
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium',
        'transition-colors motion-reduce:transition-none',
        focusRing,
        isSelected
          ? 'bg-white text-purple-700 shadow-sm'
          : 'text.body hover:text.heading hover:bg-white/60',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        className,
      ].join(' ')}
    >
      {LeadingIcon ? <LeadingIcon className="w-4 h-4" aria-hidden="true" /> : null}
      {children}
    </button>
  );
};

export const TabPanel = ({ value, children, className = '' }) => {
  const ctx = useContext(TabsContext);
  const tabId = `${ctx.idPrefix}-tab-${value}`;
  const panelId = `${ctx.idPrefix}-panel-${value}`;
  const isActive = ctx.current === value;
  if (!isActive) return null;
  return (
    <div
      id={panelId}
      role="tabpanel"
      aria-labelledby={tabId}
      tabIndex={0}
      className={[focusRing, className].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
};

export default Tabs;
